// # Budget Account Component
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../../../../auth/state';
import SharedHeader from '../../../../../components/SharedHeader';
import DashboardFooter from '../../../../../components/DashboardFooter';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../../../../../firebase/config';
import { 
  BarChart, Bar, PieChart, Pie, Cell, LineChart, Line, 
  AreaChart, Area, ComposedChart, XAxis, YAxis, CartesianGrid, 
  Tooltip, Legend, ResponsiveContainer 
} from 'recharts';
import { 
  FaChartPie, FaChartLine, FaHistory, FaMoneyBillWave, 
  FaPercentage, FaWallet, FaExchangeAlt, FaProjectDiagram,
  FaSearch, FaFilter, FaCalendarAlt, FaTable, FaServer, FaSave
} from 'react-icons/fa';
import './budget.css';

const BudgetAccount = () => {
  const navigate = useNavigate();
  const { isAdmin } = useAuthStore();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [budgetData, setBudgetData] = useState(null);
  const [fundingSourceData, setFundingSourceData] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFilter, setDateFilter] = useState({
    startDate: new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });

  // # Chart colors
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D'];

  // # Tabs configuration
  const TABS = [
    { id: 'overview', label: 'Budget Overview', icon: <FaChartPie /> },
    { id: 'allocation', label: 'Allocation Analysis', icon: <FaPercentage /> },
    { id: 'expenses', label: 'Expense Tracking', icon: <FaWallet /> },
    { id: 'timeline', label: 'Budget Timeline', icon: <FaChartLine /> },
    { id: 'funding', label: 'Funding Sources', icon: <FaMoneyBillWave /> }
  ];

  // # Fetch budget data
  useEffect(() => {
    const fetchBudgetData = async () => {
      if (!isAdmin) return;
      
      try {
        // # Initialize budget data structure
        let budgetData = {
          totalAllocation: 0,
          totalExpenses: 0,
          netBudget: 0,
          allocationByProduct: {},
          expensesByCategory: {},
          monthlyData: {}
        };
        
        // # Get all documents from the financial collection
        const financialRef = collection(db, 'financial');
        const querySnapshot = await getDocs(financialRef);
        
        // # Process each document (year_month format)
        querySnapshot.forEach(doc => {
          const yearMonthData = doc.data();
          
          // # Process payments for budget allocations
          if (yearMonthData.payments) {
            Object.entries(yearMonthData.payments).forEach(([paymentId, payment]) => {
              // # Check if payment has budget_dvd data
              if (payment.budget_dvd && payment.budget_dvd.egp_amount) {
                const amount = Number(payment.budget_dvd.egp_amount) || 0;
                
                // # Update total allocation
                budgetData.totalAllocation += amount;
                
                // # Update allocation by product
                const product = payment.pay?.product || 'unknown';
                if (!budgetData.allocationByProduct[product]) {
                  budgetData.allocationByProduct[product] = 0;
                }
                budgetData.allocationByProduct[product] += amount;
                
                // # Update monthly data
                // # Get date from metadata or fallback to current date
                const date = payment.metadata?.created_at 
                  ? new Date(payment.metadata.created_at) 
                  : new Date();
                
                const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
                
                if (!budgetData.monthlyData[monthKey]) {
                  budgetData.monthlyData[monthKey] = {
                    allocations: 0,
                    expenses: 0,
                    net: 0
                  };
                }
                
                budgetData.monthlyData[monthKey].allocations += amount;
              }
            });
          }
          
          // # Process expenses
          if (yearMonthData.expenses) {
            Object.entries(yearMonthData.expenses).forEach(([expenseId, expense]) => {
              // # Check if it's an expense transaction
              if (expense.transaction_type === 'expense') {
                const amount = Number(expense.egp_total) || 0;
                
                // # Update total expenses
                budgetData.totalExpenses += amount;
                
                // # Update expenses by category
                const category = expense.category || 'unknown';
                if (!budgetData.expensesByCategory[category]) {
                  budgetData.expensesByCategory[category] = 0;
                }
                budgetData.expensesByCategory[category] += amount;
                
                // # Update monthly data
                // # Get date from metadata or fallback to current date
                const date = expense.metadata?.created_at 
                  ? new Date(expense.metadata.created_at) 
                  : new Date();
                
                const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
                
                if (!budgetData.monthlyData[monthKey]) {
                  budgetData.monthlyData[monthKey] = {
                    allocations: 0,
                    expenses: 0,
                    net: 0
                  };
                }
                
                budgetData.monthlyData[monthKey].expenses += amount;
              }
            });
          }
        });
        
        // # Calculate net budget
        budgetData.netBudget = budgetData.totalAllocation - budgetData.totalExpenses;
        
        // # Calculate net for each month
        Object.keys(budgetData.monthlyData).forEach(monthKey => {
          const monthData = budgetData.monthlyData[monthKey];
          monthData.net = monthData.allocations - monthData.expenses;
        });
        
        // # Convert monthly data to array for charts
        const monthlyDataArray = Object.entries(budgetData.monthlyData).map(([month, data]) => ({
          month,
          allocations: data.allocations,
          expenses: data.expenses,
          net: data.net
        })).sort((a, b) => a.month.localeCompare(b.month));
        
        // # Calculate category percentages
        const totalExpenses = budgetData.totalExpenses || 1; // Avoid division by zero
        const categoryPercentages = {};
        
        Object.entries(budgetData.expensesByCategory).forEach(([category, amount]) => {
          categoryPercentages[category] = (amount / totalExpenses) * 100;
        });
        
        // # Update budget data with processed information
        const updatedBudgetData = {
          ...budgetData,
          monthlyDataArray,
          categoryPercentages
        };
        
        setBudgetData(updatedBudgetData);
      } catch (error) {
        console.error('Error fetching budget data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchBudgetData();
  }, [isAdmin]);

  // # Fetch funding source data
  useEffect(() => {
    const fetchFundingSourceData = async () => {
      if (!isAdmin) return;
      
      try {
        // Get all documents from the financial collection
        const financialRef = collection(db, 'financial');
        const querySnapshot = await getDocs(financialRef);
        
        let allPayments = [];
        
        // Process each document (year_month format)
        querySnapshot.docs.forEach(doc => {
          const yearMonthData = doc.data();
          
          // Process each payment entry in the document
          if (yearMonthData.payments) {
            Object.entries(yearMonthData.payments).forEach(([paymentId, payment]) => {
              // Check if payment has budget allocation
              if (payment.budget_dvd && payment.budget_dvd.egp_amount) {
                // Format the payment data
                const formattedPayment = {
                  id: paymentId,
                  timestamp: payment.metadata?.created_at || '',
                  customer: payment.user?.name || 'Unknown',
                  email: payment.user?.email || '',
                  product: payment.pay?.product || 'Unknown',
                  totalAmount: parseFloat(payment.pay?.egp_total) || 0,
                  budgetAllocation: parseFloat(payment.budget_dvd.egp_amount) || 0,
                  currency: payment.pay?.currency || 'EGP',
                  nationality: payment.user?.nationality || 'Unknown'
                };
                
                allPayments.push(formattedPayment);
              }
            });
          }
        });

        // Sort payments by timestamp in descending order
        allPayments.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
        setFundingSourceData(allPayments);
      } catch (error) {
        console.error('Error fetching funding source data:', error);
      }
    };

    fetchFundingSourceData();
  }, [isAdmin]);

  // # Filter funding sources based on search query and date range
  const filteredFundingSources = fundingSourceData.filter(source => {
    const matchesSearch = 
      source.customer.toLowerCase().includes(searchQuery.toLowerCase()) ||
      source.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      source.product.toLowerCase().includes(searchQuery.toLowerCase()) ||
      source.id.toLowerCase().includes(searchQuery.toLowerCase());
    
    const sourceDate = new Date(source.timestamp).toISOString().split('T')[0];
    const matchesDateRange = 
      sourceDate >= dateFilter.startDate && 
      sourceDate <= dateFilter.endDate;
    
    return matchesSearch && matchesDateRange;
  });

  // # Redirect if not admin
  React.useEffect(() => {
    if (!isAdmin) {
      navigate('/dashboard');
    }
  }, [isAdmin, navigate]);

  // # Helper function to format product names
  const formatProductName = (productName) => {
    // Shorten mentorship product names
    if (productName.includes('mentorship')) {
      return productName
        .replace('step1_mentorship_monthly', 'Step 1 Mentorship (Monthly)')
        .replace('step1_mentorship_premium', 'Step 1 Mentorship (Premium)')
        .replace('step2_mentorship_monthly', 'Step 2 Mentorship (Monthly)')
        .replace('step2_mentorship_premium', 'Step 2 Mentorship (Premium)');
    }
    
    // Shorten library product names
    if (productName.includes('library')) {
      return productName
        .replace('library_monthly', 'Library (Monthly)')
        .replace('library_premium', 'Library (Premium)');
    }
    
    // Shorten premium product names
    if (productName.includes('premium')) {
      return productName
        .replace('premium_monthly', 'Premium (Monthly)')
        .replace('premium_yearly', 'Premium (Yearly)');
    }
    
    return productName;
  };

  if (!budgetData) return null;

  // # Render tab content
  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <div className="bdgt_tab-content">
            {/* Budget Overview Stats */}
            <div className="bdgt_stats-grid">
              <div className="bdgt_stat-card total-allocation">
                <h3>Total Budget Allocation</h3>
                <p className="bdgt_stat-value">E£ {budgetData.totalAllocation.toLocaleString()}</p>
              </div>
              <div className="bdgt_stat-card expenses">
                <h3>Total Expenses</h3>
                <p className="bdgt_stat-value">E£ {budgetData.totalExpenses.toLocaleString()}</p>
              </div>
              <div className="bdgt_stat-card balance">
                <h3>Current Budget Balance</h3>
                <p className="bdgt_stat-value">E£ {budgetData.netBudget.toLocaleString()}</p>
              </div>
              <div className="bdgt_stat-card utilization">
                <h3>Budget Utilization</h3>
                <p className="bdgt_stat-value">
                  {budgetData.totalAllocation > 0 
                    ? `${((budgetData.totalExpenses / budgetData.totalAllocation) * 100).toFixed(1)}%` 
                    : '0%'}
                </p>
              </div>
            </div>

            {/* Budget Allocation vs Expenses Chart */}
            <div className="bdgt_chart-container">
              <h3>Budget Allocation vs Expenses</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={[
                  { name: 'Budget Allocation', value: budgetData.totalAllocation },
                  { name: 'Expenses', value: budgetData.totalExpenses },
                  { name: 'Remaining Balance', value: budgetData.netBudget }
                ]}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip formatter={(value) => `E£ ${value.toLocaleString()}`} />
                  <Legend />
                  <Bar dataKey="value">
                    {[
                      { fill: '#0088FE' }, // Budget Allocation
                      { fill: '#00C49F' }, // Expenses
                      { fill: '#FFBB28' }  // Remaining Balance
                    ].map((props, index) => (
                      <Cell key={`cell-${index}`} {...props} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        );

      case 'allocation':
        return (
          <div className="bdgt_tab-content">
            {/* Budget Allocation Stats */}
            <div className="bdgt_stats-grid">
              <div className="bdgt_stat-card">
                <h3>Total Budget Allocation</h3>
                <p className="bdgt_stat-value">E£ {budgetData.totalAllocation.toLocaleString()}</p>
              </div>
              <div className="bdgt_stat-card">
                <h3>Average Monthly Allocation</h3>
                <p className="bdgt_stat-value">
                  E£ {(budgetData.totalAllocation / 
                    Math.max(1, Object.keys(budgetData.monthlyData).length)).toLocaleString()}
                </p>
              </div>
              <div className="bdgt_stat-card">
                <h3>Allocation Efficiency</h3>
                <p className="bdgt_stat-value">
                  {budgetData.netBudget > 0 
                    ? `${((budgetData.netBudget / budgetData.totalAllocation) * 100).toFixed(1)}%` 
                    : '0%'}
                </p>
              </div>
              <div className="bdgt_stat-card">
                <h3>Top Product Contribution</h3>
                <p className="bdgt_stat-value">
                  {formatProductName(Object.entries(budgetData.allocationByProduct).sort((a, b) => b[1] - a[1])[0]?.[0] || 'None')}
                </p>
              </div>
            </div>

            {/* Budget Allocation by Product */}
            <div className="bdgt_chart-container">
              <h3>Budget Allocation by Product</h3>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={Object.entries(budgetData.allocationByProduct).map(([product, amount]) => ({
                      name: formatProductName(product),
                      originalName: product,
                      value: amount
                    }))}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    label={({ name, percent }) => `${name} (${percent.toFixed(1)}%)`}
                  >
                    {Object.entries(budgetData.allocationByProduct).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => `E£ ${value.toLocaleString()}`} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>

            {/* Monthly Budget Allocation Trend */}
            <div className="bdgt_chart-container">
              <h3>Monthly Budget Allocation Trend</h3>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart 
                  data={Object.entries(budgetData.monthlyData).map(([month, data]) => ({
                    month,
                    allocation: data.allocations
                  }))}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip formatter={(value) => `E£ ${value.toLocaleString()}`} />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="allocation" 
                    stroke="#8884d8" 
                    name="Monthly Budget Allocation" 
                    activeDot={{ r: 8 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Budget Allocation Distribution */}
            <div className="bdgt_chart-container">
              <h3>Budget Allocation Distribution</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart 
                  layout="vertical"
                  data={Object.entries(budgetData.allocationByProduct)
                    .sort((a, b) => b[1] - a[1])
                    .map(([product, amount]) => ({
                      product: formatProductName(product),
                      originalProduct: product,
                      amount,
                      percentage: (amount / budgetData.totalAllocation) * 100
                    }))}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis dataKey="product" type="category" width={150} />
                  <Tooltip 
                    formatter={(value, name) => [
                      `E£ ${value.toLocaleString()}`,
                      name === 'amount' ? 'Amount (EGP)' : 'Amount (EGP)'
                    ]}
                  />
                  <Legend />
                  <Bar dataKey="amount" fill="#82ca9d" name="Amount (EGP)" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        );

      case 'expenses':
        // # Create expense categories data from actual data
        const expenseCategoriesData = Object.entries(budgetData.expensesByCategory || {})
          .map(([category, amount]) => ({
            id: category,
            title: category.charAt(0).toUpperCase() + category.slice(1),
            icon: getCategoryIcon(category),
            amount: amount || 0,
            percent: budgetData.categoryPercentages?.[category] || 0
          }));

        // # Helper function to get appropriate icon for each category
        function getCategoryIcon(category) {
          switch(category) {
            case 'software': 
            case 'software maintenance': return <FaProjectDiagram />;
            case 'hosting': return <FaServer />;
            case 'subscription': return <FaMoneyBillWave />;
            case 'marketing': return <FaChartLine />;
            case 'content': 
            case 'content development': return <FaTable />;
            case 'investment': return <FaExchangeAlt />;
            case 'budget leftover': return <FaSave />;
            default: return <FaWallet />;
          }
        }

        return (
          <div className="bdgt_tab-content">
            {/* Expense Tracking Stats */}
            <div className="bdgt_stats-grid">
              <div className="bdgt_stat-card">
                <h3>Total Expenses</h3>
                <p className="bdgt_stat-value">E£ {budgetData.totalExpenses.toLocaleString()}</p>
              </div>
              <div className="bdgt_stat-card">
                <h3>Budget Remaining</h3>
                <p className="bdgt_stat-value">E£ {budgetData.netBudget.toLocaleString()}</p>
              </div>
              <div className="bdgt_stat-card">
                <h3>Expense Ratio</h3>
                <p className="bdgt_stat-value">
                  {budgetData.totalAllocation > 0 
                    ? `${((budgetData.totalExpenses / budgetData.totalAllocation) * 100).toFixed(1)}%` 
                    : '0%'}
                </p>
              </div>
              <div className="bdgt_stat-card">
                <h3>Budget Health</h3>
                <p className="bdgt_stat-value">
                  {budgetData.netBudget > 0 ? 'Positive' : 'Negative'}
                </p>
              </div>
            </div>

            {/* Expense Categories */}
            <div className="bdgt_chart-container">
              <h3>Expense Categories</h3>
              
              {Object.keys(budgetData.expensesByCategory || {}).length === 0 ? (
                <div className="bdgt_expense-note">
                  <p>
                    <strong>Note:</strong> The expense categories will show actual data once expenses are registered in the system. 
                    Currently showing zero values as no expenses have been categorized yet.
                  </p>
                </div>
              ) : null}
              
              <div className="bdgt_expense-categories">
                {expenseCategoriesData.length > 0 ? 
                  expenseCategoriesData.map(category => (
                    <div key={category.id} className={`bdgt_category-card ${category.id}`}>
                      <div className="bdgt_category-header">
                        <span className="bdgt_category-icon">{category.icon}</span>
                        <h4 className="bdgt_category-title">{category.title}</h4>
                      </div>
                      <p className="bdgt_category-amount">E£ {category.amount.toLocaleString()}</p>
                      <div className="bdgt_category-percent">
                        <span>{category.percent.toFixed(1)}% of total expenses</span>
                      </div>
                      <div className="bdgt_category-bar">
                        <div 
                          className={`bdgt_category-progress bdgt_${category.id}-progress`} 
                          style={{ width: `${category.percent}%` }}
                        ></div>
                      </div>
                    </div>
                  )) : (
                    <div className="bdgt_placeholder-text">
                      No expense data available yet. Categories will appear when expenses are registered.
                    </div>
                  )
                }
              </div>
            </div>
            
            {/* Expense Categories Chart */}
            <div className="bdgt_chart-container">
              <h3>Expense Distribution by Category</h3>
              {expenseCategoriesData.length === 0 || expenseCategoriesData.every(cat => cat.amount === 0) ? (
                <div className="bdgt_placeholder-text">
                  No expense data available yet. Chart will appear when expenses are registered.
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={expenseCategoriesData}
                      dataKey="amount"
                      nameKey="title"
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      label={({ title, percent }) => `${title} (${percent.toFixed(1)}%)`}
                    >
                      {expenseCategoriesData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => `E£ ${value.toLocaleString()}`} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        );

      case 'timeline':
        return (
          <div className="bdgt_tab-content">
            {/* Budget Timeline Chart */}
            <div className="bdgt_chart-container">
              <h3>Budget Timeline</h3>
              <ResponsiveContainer width="100%" height={400}>
                <ComposedChart data={Object.entries(budgetData.monthlyData).map(([month, data]) => ({
                  month,
                  allocation: data.allocations,
                  expenses: data.expenses,
                  balance: data.net
                }))}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip formatter={(value) => `E£ ${value.toLocaleString()}`} />
                  <Legend />
                  <Bar dataKey="allocation" fill="#8884d8" name="Budget Allocation" />
                  <Bar dataKey="expenses" fill="#82ca9d" name="Expenses" />
                  <Line type="monotone" dataKey="balance" stroke="#ff7300" name="Balance" />
                </ComposedChart>
              </ResponsiveContainer>
            </div>

            {/* Cumulative Budget Chart */}
            <div className="bdgt_chart-container">
              <h3>Cumulative Budget Balance</h3>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={Object.entries(budgetData.monthlyData).map(([month, data], index, array) => {
                  let cumulativeAllocation = 0;
                  let cumulativeExpenses = 0;
                  
                  for (let i = 0; i <= index; i++) {
                    const [_, monthData] = array[i];
                    cumulativeAllocation += monthData.allocations;
                    cumulativeExpenses += monthData.expenses;
                  }
                  
                  return {
                    month,
                    cumulativeAllocation,
                    cumulativeExpenses,
                    cumulativeBalance: cumulativeAllocation - cumulativeExpenses
                  };
                })}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip formatter={(value) => `E£ ${value.toLocaleString()}`} />
                  <Legend />
                  <Area 
                    type="monotone" 
                    dataKey="cumulativeAllocation" 
                    stackId="1"
                    stroke="#8884d8" 
                    fill="#8884d8" 
                    name="Cumulative Budget Allocation"
                  />
                  <Area 
                    type="monotone" 
                    dataKey="cumulativeExpenses" 
                    stackId="2"
                    stroke="#82ca9d" 
                    fill="#82ca9d" 
                    name="Cumulative Expenses"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="cumulativeBalance" 
                    stroke="#ff7300" 
                    name="Cumulative Balance"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        );

      case 'funding':
        return (
          <div className="bdgt_tab-content">
            {/* Funding Sources Overview */}
            <div className="bdgt_funding-overview">
              <h3>Budget Funding Sources</h3>
              <p className="bdgt_funding-description">
                Track all revenue sources that contribute to the Budget
              </p>
              
              {/* Funding Sources Stats */}
              <div className="bdgt_stats-grid">
                <div className="bdgt_stat-card">
                  <h3>Total Funding Transactions</h3>
                  <p className="bdgt_stat-value">{fundingSourceData.length}</p>
                </div>
                <div className="bdgt_stat-card">
                  <h3>Total Budget Allocation</h3>
                  <p className="bdgt_stat-value">
                    E£ {fundingSourceData.reduce((sum, source) => sum + source.budgetAllocation, 0).toLocaleString()}
                  </p>
                </div>
                <div className="bdgt_stat-card">
                  <h3>Average Allocation</h3>
                  <p className="bdgt_stat-value">
                    E£ {(fundingSourceData.reduce((sum, source) => sum + source.budgetAllocation, 0) / 
                        Math.max(1, fundingSourceData.length)).toLocaleString()}
                  </p>
                </div>
              </div>
              
              {/* Funding Sources by Product Chart */}
              <div className="bdgt_chart-container">
                <h3>Budget Allocation by Product</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={Object.entries(
                        fundingSourceData.reduce((acc, source) => {
                          acc[source.product] = (acc[source.product] || 0) + source.budgetAllocation;
                          return acc;
                        }, {})
                      ).map(([product, amount]) => ({
                        name: product,
                        value: amount
                      }))}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      label={({ name, percent }) => `${name} (${percent.toFixed(1)}%)`}
                    >
                      {Object.entries(
                        fundingSourceData.reduce((acc, source) => {
                          acc[source.product] = (acc[source.product] || 0) + source.budgetAllocation;
                          return acc;
                        }, {})
                      ).map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => `E£ ${value.toLocaleString()}`} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              
              {/* Funding Sources by Nationality Chart */}
              <div className="bdgt_chart-container">
                <h3>Budget Allocation by Customer Nationality</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart 
                    data={Object.entries(
                      fundingSourceData.reduce((acc, source) => {
                        acc[source.nationality] = (acc[source.nationality] || 0) + source.budgetAllocation;
                        return acc;
                      }, {})
                    ).map(([nationality, amount]) => ({
                      nationality,
                      amount
                    }))}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="nationality" />
                    <YAxis />
                    <Tooltip formatter={(value) => `E£ ${value.toLocaleString()}`} />
                    <Legend />
                    <Bar dataKey="amount" fill="#8884d8" name="Budget Allocation (EGP)" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              
              {/* Funding Sources Table */}
              <div className="bdgt_funding-table-container">
                <div className="bdgt_funding-table-header">
                  <h3>Detailed Funding Sources</h3>
                  <div className="bdgt_funding-filters">
                    <div className="bdgt_search-bar">
                      <FaSearch className="bdgt_search-icon" />
                      <input
                        type="text"
                        placeholder="Search by customer, product, or ID..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="bdgt_search-input"
                      />
                    </div>
                    <div className="bdgt_date-filter">
                      <FaCalendarAlt className="bdgt_filter-icon" />
                      <input
                        type="date"
                        value={dateFilter.startDate}
                        onChange={(e) => setDateFilter({...dateFilter, startDate: e.target.value})}
                        className="bdgt_date-input"
                      />
                      <span>to</span>
                      <input
                        type="date"
                        value={dateFilter.endDate}
                        onChange={(e) => setDateFilter({...dateFilter, endDate: e.target.value})}
                        className="bdgt_date-input"
                      />
                    </div>
                  </div>
                </div>
                
                <div className="bdgt_funding-table-wrapper">
                  <table className="bdgt_funding-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Customer</th>
                        <th>Product</th>
                        <th>Total Amount</th>
                        <th>Budget Allocation</th>
                        <th>Nationality</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredFundingSources.map((source, index) => (
                        <tr key={index}>
                          <td>{new Date(source.timestamp).toLocaleDateString()}</td>
                          <td title={source.email}>{source.customer}</td>
                          <td>{source.product}</td>
                          <td>E£ {source.totalAmount.toLocaleString()}</td>
                          <td>E£ {source.budgetAllocation.toLocaleString()}</td>
                          <td>{source.nationality}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {filteredFundingSources.length === 0 && (
                  <div className="bdgt_no-results">
                    <p>No funding sources match your search criteria.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="bdgt_page">
      <SharedHeader />
      
      <div className="bdgt_header">
        <div className="bdgt_header-content">
          <h1>Project Budget Account</h1>
          <p className="bdgt_header-description">
            Managing the Budget Allocation from all revenue streams
          </p>
        </div>
        <button 
          className="bdgt_back-button"
          onClick={() => navigate('/admin/financial/bank')}
        >
          Back to Bank Accounts
        </button>
      </div>

      <main className="bdgt_content">
        <div className="bdgt_tabs">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              className={`bdgt_tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>

        <div className="bdgt_tab-container">
          {loading ? (
            <div className="bdgt_loading">Loading budget data...</div>
          ) : (
            renderTabContent()
          )}
        </div>
      </main>

      <DashboardFooter />
    </div>
  );
};

export default BudgetAccount;
